import { useEffect, useState } from "react"

import {
  loadPortfolioEntries,
  type PortfolioEntryRow,
} from "@/admin/companies/portfolio-entries-data"
import { PortfolioEntriesSheet } from "@/admin/companies/portfolio-entries-sheet"
import { Badge } from "@/components/ui/badge"
import { errorMessage } from "@/lib/error-message"

// A tag per Portfolio that holds the Company; each opens the Portfolio editor,
// and closing it reloads the tags.
export const PortfolioTags = ({
  company,
}: {
  company: { companyId: string; displayName: string }
}) => {
  const [entries, setEntries] = useState<PortfolioEntryRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const { companyId } = company
  useEffect(() => {
    if (!isEditing)
      loadPortfolioEntries({ data: { companyId } })
        .then(setEntries)
        .catch((cause: unknown) => setError(errorMessage(cause)))
  }, [companyId, isEditing])
  if (error)
    return (
      <p role="alert" className="text-assessment-critical text-xs">
        Portfolios failed to load: {error}
      </p>
    )
  if (!entries) return null
  const tags =
    entries.length > 0
      ? entries.map((entry) => ({
          id: entry.id,
          label: `${entry.portfolioName} · ${entry.isMonitoring ? "Monitored" : "Snapshot"}`,
          isMonitoring: entry.isMonitoring,
        }))
      : [{ id: "none", label: "No Portfolio", isMonitoring: false }]
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tags.map((tag) => (
        <Badge
          key={tag.id}
          variant={tag.isMonitoring ? "secondary" : "outline"}
          shouldRenderAsChild
        >
          <button
            type="button"
            title="Edit Portfolios"
            onClick={() => setIsEditing(true)}
          >
            {tag.label}
          </button>
        </Badge>
      ))}
      <PortfolioEntriesSheet
        company={isEditing ? company : null}
        onClose={() => setIsEditing(false)}
      />
    </div>
  )
}
